'use client';

import React, { useState, useCallback, useEffect, useRef } from 'react';
import { PDFDocument } from 'pdf-lib';
import JSZip from 'jszip';
import Icon from '@/components/ui/AppIcon';
import { useToast } from '@/components/ui/Toast';
import { getPdfMeta, PDF_CONFIG, useEncryptedPdfToast } from '@/constants/pdfConfig';
import UploadZone from '@/components/pdf/UploadZone';
import { isEncryptedPDF } from '@/utils/pdf';
import Script from 'next/script';
import PDFViewerModal from '@/components/pdf/PDFViewerModal';
import { FileCard } from '@/components/pdf/FileCard';
// import { ensurePdfWorker, getPdfjsLib, renderPageThumbnail } from '@/utils/pdfThumbnailUtils';

interface UploadedFile {
  id: string;
  name: string;
  size: number;
  type?: string;
  file: File;
  pageCount: number;
}

interface PageThumbnail {
  id: string;
  pageIndex: number;
  url: string;
}

type SplitMode = 'ranges' | 'fixed' | 'every';

// Blue Theme Constants
const SPLIT_THEME = {
  primary: '#3B82F6',
  primaryLight: '#EFF6FF',
  primaryBorder: '#BFDBFE',
  primaryShadow: 'rgba(59, 130, 246, 0.3)',
};

export default function SplitPDFUploader() {
  const [file, setFile] = useState<UploadedFile | null>(null);
  const [thumbnails, setThumbnails] = useState<PageThumbnail[]>([]);
  const [isProcessing, setIsProcessing] = useState(false);
  const [progress, setProgress] = useState(0);
  const [isDone, setIsDone] = useState(false);
  const [splitMode, setSplitMode] = useState<SplitMode>('ranges');
  const [rangeInput, setRangeInput] = useState('1');
  const [fixedInterval, setFixedInterval] = useState(1);
  const [downloadCount, setDownloadCount] = useState(0);
  const [viewerOpen, setViewerOpen] = useState(false);
  const [currentPage, setCurrentPage] = useState(0);

  const { showToast } = useToast();
  const { showEncryptedPdfToast } = useEncryptedPdfToast();
  const downloadTriggeredRef = useRef(false);

  // Cleanup thumbnails on unmount
  useEffect(() => {
    return () => {
      thumbnails.forEach((t) => URL.revokeObjectURL(t.url));
    };
  }, [thumbnails]);

  // Reset function for after download
  const resetAfterDownload = useCallback(() => {
    // Clean up thumbnails
    thumbnails.forEach((t) => URL.revokeObjectURL(t.url));
    setThumbnails([]);
    setFile(null);
    setIsDone(false);
    setProgress(0);
    setRangeInput('1');
    setFixedInterval(1);
    setSplitMode('ranges');
    setDownloadCount(0);
    downloadTriggeredRef.current = false;

    showToast('Ready to split another PDF!', 'info');
  }, [thumbnails, showToast]);

  /** Handle File Upload (Single File Only) */
  const handleFiles = useCallback(
    async (newFiles: FileList | null) => {
      if (!newFiles || newFiles.length === 0) return;

      const f = newFiles[0];

      if (
        !(f.type === 'application/pdf' || f.name.toLowerCase().endsWith('.pdf')) ||
        f.size > PDF_CONFIG.MAX_FILE_SIZE
      ) {
        showToast('Invalid file type or file too large (max 100MB).', 'error');
        return;
      }

      try {
        // Pre-flight: check for encryption
        const alreadyEncrypted = await isEncryptedPDF(f);
        if (alreadyEncrypted) {
          showEncryptedPdfToast('splitting');
          return;
        }

        // Cleanup old thumbnails
        thumbnails.forEach((t) => URL.revokeObjectURL(t.url));
        setThumbnails([]);

        const meta = await getPdfMeta(f);
        setFile(meta);
        setIsDone(false);
        setProgress(0);
        setDownloadCount(0);
        downloadTriggeredRef.current = false;
      } catch (err) {
        console.error('Error reading PDF:', err);
        showToast('Failed to read PDF file.', 'error');
      }
    },
    [thumbnails, showToast]
  );

  /** Generate Thumbnails (Lazy) */
  useEffect(() => {
    if (!file) return;

    let cancelled = false;
    const loadedThumbs: PageThumbnail[] = [];

    const generateThumbnailsLazy = async () => {
      try {
        const bytes = await file.file.arrayBuffer();
        const pdfjsLib = (window as any).pdfjsLib;
        if (!pdfjsLib) return;

        if (!pdfjsLib.GlobalWorkerOptions.workerSrc) {
          pdfjsLib.GlobalWorkerOptions.workerSrc =
            'https://cdnjs.cloudflare.com/ajax/libs/pdf.js/4.10.38/pdf.worker.min.mjs';
        }

        const loadingTask = pdfjsLib.getDocument({ data: bytes });
        const pdf = await loadingTask.promise;

        for (let i = 1; i <= pdf.numPages; i++) {
          if (cancelled) break;

          const page = await pdf.getPage(i);
          const viewport = page.getViewport({ scale: 0.4 });

          const canvas = document.createElement('canvas');
          const context = canvas.getContext('2d');
          if (!context) continue;

          canvas.width = viewport.width;
          canvas.height = viewport.height;

          await page.render({ canvasContext: context, viewport }).promise;

          const blob = await new Promise<Blob | null>((resolve) =>
            canvas.toBlob(resolve, 'image/jpeg', 0.8)
          );
          if (!blob) continue;

          const url = URL.createObjectURL(blob);
          const thumb: PageThumbnail = { id: crypto.randomUUID(), pageIndex: i - 1, url };
          loadedThumbs.push(thumb);

          setThumbnails([...loadedThumbs]);

          // Small delay for main thread responsiveness
          await new Promise((r) => setTimeout(r, 10));
        }
      } catch (err) {
        console.error('Thumbnail generation error:', err);
      }
    };

    generateThumbnailsLazy();

    return () => {
      cancelled = true;
      loadedThumbs.forEach((t) => URL.revokeObjectURL(t.url));
    };
  }, [file]);

  const removeFile = useCallback(() => {
    thumbnails.forEach((t) => URL.revokeObjectURL(t.url));
    setThumbnails([]);
    setFile(null);
    setIsDone(false);
    setProgress(0);
    setRangeInput('1');
    setFixedInterval(1);
    setDownloadCount(0);
    downloadTriggeredRef.current = false;
  }, [thumbnails]);

  /** Parsing Logic */
  const parseRanges = useCallback((input: string, totalPages: number): number[][] => {
    const segments = input.split(',').map((s) => s.trim());
    const ranges: number[][] = [];

    for (const segment of segments) {
      if (segment.includes('-')) {
        const [startStr, endStr] = segment.split('-');
        let start = parseInt(startStr);
        let end = parseInt(endStr);

        if (isNaN(start) || isNaN(end)) continue;

        // Convert to 0-indexed
        start = Math.max(1, start) - 1;
        end = Math.min(totalPages, end) - 1;

        if (start <= end) {
          const range = [];
          for (let i = start; i <= end; i++) range.push(i);
          ranges.push(range);
        }
      } else {
        const page = parseInt(segment);
        if (!isNaN(page) && page >= 1 && page <= totalPages) {
          ranges.push([page - 1]);
        }
      }
    }
    return ranges;
  }, []);

  /** Split Functions */
  const handleSplit = async () => {
    if (!file) return;

    try {
      setIsProcessing(true);
      setProgress(0);
      downloadTriggeredRef.current = false;

      const arrayBuffer = await file.file.arrayBuffer();
      const mainPdfDoc = await PDFDocument.load(arrayBuffer);
      const totalPagesScope = file.pageCount;

      let rangesToExtract: number[][] = [];

      if (splitMode === 'every') {
        for (let i = 0; i < totalPagesScope; i++) {
          rangesToExtract.push([i]);
        }
      } else if (splitMode === 'fixed') {
        const interval = Math.max(1, fixedInterval);
        for (let i = 0; i < totalPagesScope; i += interval) {
          const range = [];
          for (let j = i; j < i + interval && j < totalPagesScope; j++) {
            range.push(j);
          }
          rangesToExtract.push(range);
        }
      } else {
        rangesToExtract = parseRanges(rangeInput, totalPagesScope);
      }

      if (rangesToExtract.length === 0) {
        showToast('Please provide valid page ranges.', 'error');
        setIsProcessing(false);
        return;
      }

      // Analytics tracking
      if (typeof window !== 'undefined' && (window as any).clarity) {
        (window as any).clarity('set', 'split_mode', splitMode);
        (window as any).clarity('set', 'split_output_count', rangesToExtract.length);
      }

      const zip = new JSZip();
      const baseName = file.name.replace(/\.[^/.]+$/, '');

      for (let i = 0; i < rangesToExtract.length; i++) {
        const pages = rangesToExtract[i];
        const newPdf = await PDFDocument.create();
        const copiedPages = await newPdf.copyPages(mainPdfDoc, pages);
        copiedPages.forEach((p) => newPdf.addPage(p));

        const pdfBytes = await newPdf.save();

        if (rangesToExtract.length === 1) {
          // Single file extraction - Direct Download
          const blob = new Blob([pdfBytes as any], { type: 'application/pdf' });
          const url = URL.createObjectURL(blob);
          const a = document.createElement('a');
          a.href = url;
          a.download = `${baseName}_split.pdf`;
          document.body.appendChild(a);
          a.click();
          document.body.removeChild(a);

          // Mark download as triggered
          downloadTriggeredRef.current = true;
          setDownloadCount((prev) => prev + 1);

          setTimeout(() => URL.revokeObjectURL(url), 100);
        } else {
          // Multiple files - Add to ZIP
          zip.file(`${baseName}_part_${i + 1}.pdf`, pdfBytes);
        }

        setProgress(Math.round(((i + 1) / rangesToExtract.length) * 100));
        // Small delay to allow UI updates
        await new Promise((resolve) => setTimeout(resolve, 10));
      }

      if (rangesToExtract.length > 1) {
        const zipBlob = await zip.generateAsync({ type: 'blob' });
        const url = URL.createObjectURL(zipBlob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `${baseName}_split.zip`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);

        // Mark download as triggered
        downloadTriggeredRef.current = true;
        setDownloadCount((prev) => prev + 1);

        setTimeout(() => URL.revokeObjectURL(url), 100);
      }

      setIsDone(true);
      showToast('PDF split successfully!', 'success');
    } catch (err) {
      console.error('Split error:', err);
      showToast('An error occurred during splitting.', 'error');
    } finally {
      setIsProcessing(false);
    }
  };

  // Handle download again - triggers reset after download
  const handleDownloadAgain = useCallback(async () => {
    if (!file) return;

    // Re-trigger the split with same settings
    await handleSplit();

    // After download, reset if this was the first download
    if (downloadCount === 0) {
      // Will be handled by the download trigger in handleSplit
    }
  }, [file, handleSplit, downloadCount]);

  // Auto-reset after download completion (when user clicks "Start New Task")
  const handleStartNewTask = useCallback(() => {
    resetAfterDownload();
  }, [resetAfterDownload]);

  return (
    <div className="w-full max-w-6xl mx-auto px-4">
      <Script
        src="https://cdnjs.cloudflare.com/ajax/libs/pdf.js/4.10.38/pdf.min.mjs"
        type="module"
        strategy="afterInteractive"
      />

      {file && (
        <PDFViewerModal
          file={file}
          isOpen={viewerOpen}
          currentPage={currentPage}
          onClose={() => setViewerOpen(false)}
          onPageChange={setCurrentPage}
        />
      )}

      {/* Upload Zone */}
      {!file && (
        <UploadZone
          onFilesSelected={handleFiles}
          multiple={false}
          accentColor={SPLIT_THEME.primary}
          iconName="ScissorsIcon"
          title="Drop PDF file here"
          subtitle="or click to browse — single file only"
          buttonText="Select PDF File"
          dragTitle="Drop your PDF here!"
        />
      )}

      {/* ================= MAIN WORK AREA ================= */}
      {file && !isDone && (
        <div className="flex flex-col lg:flex-row gap-6 mt-4">
          {/* ================= LEFT SIDE ================= */}
          <div className="flex-1 min-w-0">
            {/* Header */}
            <FileCard file={file} onRemove={removeFile} theme={SPLIT_THEME} />

            {/* Scrollable Thumbnails */}
            <div className="max-h-[70vh] overflow-y-auto pr-2 pb-20 lg:pb-0">
              <div className="grid grid-cols-2 xs:grid-cols-3 sm:grid-cols-4 md:grid-cols-5 lg:grid-cols-4 xl:grid-cols-5 gap-3">
                {Array.from({ length: file.pageCount }).map((_, i) => {
                  const thumb = thumbnails.find((t) => t.pageIndex === i);
                  return (
                    <div
                      key={i}
                      className="relative border rounded-xl overflow-hidden aspect-[100/140] group cursor-pointer transition"
                      onClick={() => {
                        setCurrentPage(i);
                        setViewerOpen(true);
                      }}
                    >
                      {thumb ? (
                        <img
                          src={thumb.url}
                          alt={`Page ${i + 1} preview`}
                          className="w-full h-full object-cover"
                          loading="lazy"
                        />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center animate-pulse bg-brand-surface">
                          <Icon name="DocumentIcon" size={24} className="text-brand-border" />
                        </div>
                      )}

                      <div className="absolute inset-0 bg-black/30 opacity-0 group-hover:opacity-100 transition flex items-center justify-center pointer-events-none">
                        <Icon name="EyeIcon" size={22} className="text-white" />
                      </div>

                      <span className="absolute bottom-1 left-0 right-0 text-center text-[10px] text-brand-muted">
                        {i + 1}
                      </span>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>

          {/* ================= RIGHT SIDEBAR ================= */}
          <div className="w-full lg:w-[280px]">
            <div className="lg:sticky lg:top-6 bg-white border border-brand-border rounded-3xl p-6 shadow-card">
              {/* ALL CONTENT BELOW IS EXACT SAME */}

              <h3 className="font-heading font-bold text-lg text-brand-dark mb-5 flex items-center gap-2">
                <Icon name="ScissorsIcon" size={20} style={{ color: SPLIT_THEME.primary }} />
                Split Settings
              </h3>

              {/* Mode Selectors */}
              <div className="space-y-2.5 mb-6">
                {[
                  {
                    id: 'ranges',
                    label: 'Custom Ranges',
                    icon: 'AdjustmentsHorizontalIcon',
                    desc: 'Select specific page groups',
                  },
                  {
                    id: 'fixed',
                    label: 'Fixed Intervals',
                    icon: 'Square2StackIcon',
                    desc: 'Split every X pages',
                  },
                  {
                    id: 'every',
                    label: 'Extract All',
                    icon: 'DocumentDuplicateIcon',
                    desc: 'Every page as a new PDF',
                  },
                ].map((mode) => (
                  <button
                    key={mode.id}
                    onClick={() => setSplitMode(mode.id as SplitMode)}
                    className={`w-full group flex items-start gap-3 p-3 rounded-2xl border transition-all text-left focus:outline-none focus-visible:ring-2 focus-visible:ring-[#3B82F6] focus-visible:ring-offset-2 ${
                      splitMode === mode.id
                        ? 'shadow-sm dark:shadow-sm-light bg-white border-gray-300'
                        : 'bg-transparent border-transparent hover:bg-brand-surface'
                    }`}
                    aria-pressed={splitMode === mode.id}
                    aria-label={`Select ${mode.label} mode - ${mode.desc}`}
                  >
                    <div
                      className={`p-2 rounded-xl mt-0.5 transition-all ${
                        splitMode === mode.id
                          ? 'text-white'
                          : 'bg-brand-surface text-brand-muted group-hover:bg-white'
                      }`}
                      style={{
                        backgroundColor: splitMode === mode.id ? SPLIT_THEME.primary : undefined,
                        boxShadow:
                          splitMode === mode.id
                            ? '0 2px 8px ' + SPLIT_THEME.primaryShadow
                            : undefined,
                      }}
                    >
                      <Icon name={mode.icon as any} size={18} aria-hidden="true" />
                    </div>
                    <div className="flex flex-col gap-0.5">
                      <span
                        className={`font-heading font-bold text-sm ${
                          splitMode === mode.id ? 'text-brand-dark' : 'text-brand-mid'
                        }`}
                      >
                        {mode.label}
                      </span>
                      <span className="text-[10.5px] text-brand-muted font-body leading-tight">
                        {mode.desc}
                      </span>
                    </div>
                  </button>
                ))}
              </div>

              {/* Mode Content */}
              <div className="mb-8 p-4 rounded-2xl bg-brand-surface border border-brand-border">
                {splitMode === 'ranges' && (
                  <div className="space-y-4">
                    <div className="space-y-2">
                      <label
                        htmlFor="range-input"
                        className="text-xs font-bold text-brand-dark uppercase tracking-wider px-1"
                      >
                        Define Ranges
                      </label>
                      <input
                        id="range-input"
                        type="text"
                        value={rangeInput}
                        onChange={(e) => setRangeInput(e.target.value)}
                        placeholder="e.g. 1-3, 5, 8-10"
                        className="w-full px-4 py-3 rounded-xl border border-brand-border focus:ring-2 outline-none transition-all font-body text-sm bg-white"
                        style={{ '--tw-ring-color': SPLIT_THEME.primary } as React.CSSProperties}
                        aria-label="Page ranges input"
                      />
                    </div>
                    <div className="space-y-2">
                      <p className="text-[11px] font-bold text-brand-muted uppercase tracking-tight px-1 flex items-center gap-1.5">
                        <Icon name="InformationCircleIcon" size={12} aria-hidden="true" />
                        How to use:
                      </p>
                      <ul className="text-[11px] text-brand-mid space-y-1.5 px-1 leading-relaxed list-disc list-inside">
                        <li>
                          Commas for separate files:{' '}
                          <code className="bg-brand-border/50 px-1 rounded text-brand-dark">
                            1-3, 5-7
                          </code>
                        </li>
                        <li>
                          Hyphens for ranges:{' '}
                          <code className="bg-brand-border/50 px-1 rounded text-brand-dark">
                            1-10
                          </code>
                        </li>
                        <li>
                          Single pages:{' '}
                          <code className="bg-brand-border/50 px-1 rounded text-brand-dark">
                            1, 3, 5
                          </code>
                        </li>
                      </ul>
                    </div>
                  </div>
                )}

                {splitMode === 'fixed' && (
                  <div className="space-y-4">
                    <div className="space-y-2">
                      <label
                        htmlFor="fixed-interval"
                        className="text-xs font-bold text-brand-dark uppercase tracking-wider px-1"
                      >
                        Split every
                      </label>
                      <div className="flex items-center gap-3">
                        <input
                          id="fixed-interval"
                          type="number"
                          min="1"
                          max={file.pageCount}
                          value={fixedInterval}
                          onChange={(e) => setFixedInterval(parseInt(e.target.value) || 1)}
                          className="flex-1 px-4 py-3 rounded-xl border border-brand-border focus:ring-2 outline-none transition-all font-heading font-bold text-center bg-white"
                          style={{ '--tw-ring-color': SPLIT_THEME.primary } as React.CSSProperties}
                          aria-label="Fixed interval value"
                        />
                        <span className="text-brand-dark font-semibold text-sm">pages</span>
                      </div>
                    </div>
                    <p className="text-[11px] text-brand-muted leading-relaxed px-1 text-center italic">
                      Example: Splitting a 10-page Doc by 2 will create 5 separate files.
                    </p>
                  </div>
                )}

                {splitMode === 'every' && (
                  <div className="space-y-3 text-center">
                    <div
                      className="w-12 h-12 rounded-full flex items-center justify-center mx-auto"
                      style={{
                        backgroundColor: SPLIT_THEME.primaryLight,
                        color: SPLIT_THEME.primary,
                      }}
                    >
                      <Icon name="DocumentDuplicateIcon" size={24} aria-hidden="true" />
                    </div>
                    <p className="text-xs text-brand-mid leading-relaxed font-body">
                      Every page will be extracted into its own separate PDF file.
                    </p>
                    <p className="text-[10px] text-brand-muted bg-white border border-brand-border px-2 py-1.5 rounded-lg inline-block">
                      Results delivered in a{' '}
                      <span className="font-bold text-brand-dark">ZIP archive</span>.
                    </p>
                  </div>
                )}
              </div>

              {/* Action Area */}
              {isProcessing ? (
                <div className="space-y-3">
                  <div className="flex justify-between text-xs font-bold text-brand-dark px-1">
                    <span className="flex items-center gap-2">
                      <div
                        className="w-1.5 h-1.5 rounded-full animate-pulse"
                        style={{ backgroundColor: SPLIT_THEME.primary }}
                      />
                      Splitting...
                    </span>
                    <span>{progress}%</span>
                  </div>
                  <div className="h-2 w-full bg-brand-surface border border-brand-border rounded-full overflow-hidden">
                    <div
                      className="h-full transition-all duration-300"
                      style={{ width: `${progress}%`, backgroundColor: SPLIT_THEME.primary }}
                    />
                  </div>
                </div>
              ) : (
                <button
                  onClick={handleSplit}
                  className="w-full py-4 rounded-2xl text-white font-heading font-bold"
                  style={{
                    backgroundColor: SPLIT_THEME.primary,
                    boxShadow: '0 6px 20px ' + SPLIT_THEME.primaryShadow,
                  }}
                >
                  Split PDF
                </button>
              )}
            </div>
          </div>
        </div>
      )}

      {/* DONE STATE (unchanged) */}
      {isDone && file && (
        <div className="text-center py-8">
          <div className="w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4 pulse-anim bg-green-50">
            <Icon name="CheckBadgeIcon" size={42} variant="solid" className="text-green-600" />
          </div>

          <h3 className="font-heading font-bold mb-2 text-[20px] text-brand-dark">
            Split Complete!
          </h3>
          <p className="text-brand-mid text-[14px] font-body mb-6">
            Your PDF has been successfully split into separate files.
          </p>

          <div className="flex flex-col sm:flex-row gap-3 justify-center items-center">
            <button
              onClick={handleDownloadAgain}
              className="sm:w-auto flex items-center justify-center gap-2 px-8 py-4 rounded-full bg-primary text-white border-none font-heading font-bold text-[15px] cursor-pointer shadow-[0_6px_20px_rgba(232,68,90,0.28)]"
              style={{
                backgroundColor: SPLIT_THEME.primary,
                boxShadow: '0 6px 20px ' + SPLIT_THEME.primaryShadow,
              }}
              aria-label="Download again"
            >
              <Icon name="ArrowDownTrayIcon" size={18} variant="solid" aria-hidden="true" />
              Download Split PDFs
            </button>
            <button
              onClick={handleStartNewTask}
              className="px-8 py-4 rounded-full border-2 border-brand-border font-heading font-bold text-brand-mid hover:bg-brand-surface transition-all active:scale-95 flex items-center justify-center gap-2 focus:outline-none focus-visible:ring-2 focus-visible:ring-[#3B82F6] focus-visible:ring-offset-2"
              aria-label="Start a new split task"
            >
              <Icon name="ArrowPathIcon" size={18} aria-hidden="true" />
              Split Another PDF
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
